import { useState, type FormEvent } from 'react'
import axios from 'axios'
import { AdvancedOptions } from './QueryInput/AdvancedOptions'
import { JsonEditor } from './QueryInput/JsonEditor'
import { ContextFilters } from './QueryInput/ContextFilters'
import { AnswerView } from './ResultDisplay/AnswerView'
import { SourcesList } from './ResultDisplay/SourcesList'
import { QueryHistoryList } from './History/QueryHistoryList'
import { ChevronDown, ChevronUp, LayoutPanelLeft } from 'lucide-react'

const API_BASE = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'

interface RagQueryParams {
    user_query: string
    scope: string
    top_k?: number
    min_score?: number
    provider?: string
    model?: string
    llm_params?: any
}

interface QueryConsoleProps {
    onInspectTrace?: (traceId: string) => void
}

export function QueryConsole({ onInspectTrace }: QueryConsoleProps) {
    // Basic Input
    const [query, setQuery] = useState('')
    const [scope, setScope] = useState('user')

    // Advanced Input
    const [showAdvanced, setShowAdvanced] = useState(false)
    const [showHistory, setShowHistory] = useState(false)
    const [topK, setTopK] = useState(5)
    const [minScore, setMinScore] = useState(0.5)
    const [model, setModel] = useState('gpt-4')
    const [llmParams, setLlmParams] = useState('{\n  "temperature": 0.7\n}')

    // API State
    const [isLoading, setIsLoading] = useState(false)
    const [result, setResult] = useState<any>(null)
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        setError(null)
        setResult(null)

        try {
            let parsedParams = {}
            try {
                parsedParams = JSON.parse(llmParams)
            } catch (e) {
                // Ignore json parse error
            }

            const params: RagQueryParams = {
                user_query: query,
                scope,
                top_k: topK,
                min_score: minScore,
                model: model,
                llm_params: parsedParams
            }

            const { data } = await axios.post(`${API_BASE}/api/v1/rag/answer`, params)
            setResult(data)
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Erro ao executar query')
            console.error(err)
        } finally {
            setIsLoading(false)
        }
    }

    const handleRestoreHistory = (q: string) => {
        setQuery(q)
    }

    return (
        <div className="max-w-[1600px] mx-auto h-[calc(100vh-140px)] flex gap-6">

            {/* HISTORY SIDEBAR (Optional) */}
            {showHistory && (
                <div className="w-64 flex-shrink-0 animate-in slide-in-from-left duration-200">
                    <QueryHistoryList
                        onSelectQuery={handleRestoreHistory}
                        lastQuery={result ? query : undefined}
                        lastStatus={result?.status}
                    />
                </div>
            )}

            {/* MAIN GRID */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-w-0">

                {/* LEFT COLUMN: Input (4/12) */}
                <div className="lg:col-span-4 flex flex-col gap-4 overflow-y-auto pr-2">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-white">Query Builder</h2>
                            <button
                                onClick={() => setShowHistory(!showHistory)}
                                className={`p-2 rounded-lg transition-colors ${showHistory ? 'bg-blue-500/20 text-blue-300' : 'hover:bg-white/10 text-gray-400'}`}
                                title="Toggle History"
                            >
                                <LayoutPanelLeft className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Query
                                </label>
                                <textarea
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Digite sua pergunta..."
                                    className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none h-32 text-sm"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Scope
                                </label>
                                <select
                                    value={scope}
                                    onChange={(e) => setScope(e.target.value)}
                                    className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-blue-500 text-sm"
                                >
                                    <option value="user">User Session</option>
                                    <option value="agent">Agent Knowledge</option>
                                </select>
                            </div>

                            {/* Advanced Toggle */}
                            <div className="border-t border-white/10 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors w-full justify-between"
                                >
                                    <span>Advanced Options</span>
                                    {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>

                                {showAdvanced && (
                                    <div className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                        <AdvancedOptions
                                            topK={topK}
                                            onTopKChange={setTopK}
                                            minScore={minScore}
                                            onMinScoreChange={setMinScore}
                                            model={model}
                                            onModelChange={setModel}
                                        />
                                        <ContextFilters />
                                        <JsonEditor
                                            value={llmParams}
                                            onChange={setLlmParams}
                                        />
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium rounded-lg transition-colors shadow-lg shadow-blue-900/20"
                            >
                                {isLoading ? 'Executando...' : 'Executar Query'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* RIGHT COLUMN: Output (8/12) */}
                <div className="lg:col-span-8 flex flex-col gap-4 overflow-y-auto h-full pb-20">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 min-h-full flex flex-col">
                        <h2 className="text-xl font-semibold text-white mb-4">Resultados</h2>

                        {error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 mb-4">
                                {error}
                            </div>
                        )}

                        {!result && !isLoading && !error && (
                            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm italic">
                                Execute uma query para ver os resultados aqui.
                            </div>
                        )}

                        {isLoading && (
                            <div className="flex-1 flex items-center justify-center">
                                <div className="text-center">
                                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                    <p className="text-gray-400">Consultando Knowledge Base...</p>
                                </div>
                            </div>
                        )}

                        {result && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                {/* Rich Answer View */}
                                <AnswerView answer={result.answer} />

                                {/* Metrics Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                                        <span className="text-[10px] uppercase text-gray-500 tracking-wider">Status</span>
                                        <p className={`font-mono mt-1 ${result.status === 'ACCEPTED' ? 'text-green-400' : 'text-yellow-400'}`}>{result.status}</p>
                                    </div>
                                    <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                                        <span className="text-[10px] uppercase text-gray-500 tracking-wider">Attempts</span>
                                        <p className="text-white font-mono mt-1">{result.attempts}</p>
                                    </div>
                                    <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                                        <span className="text-[10px] uppercase text-gray-500 tracking-wider">Latency</span>
                                        <p className="text-blue-300 font-mono mt-1">{result.total_latency_ms?.toFixed(0) || '--'}ms</p>
                                    </div>
                                    <div
                                        className="p-3 bg-white/5 border border-white/10 rounded-lg cursor-pointer hover:bg-blue-500/10 hover:border-blue-500/30 transition-colors group"
                                        onClick={() => result.request_id && onInspectTrace?.(result.request_id)}
                                    >
                                        <span className="text-[10px] uppercase text-gray-500 tracking-wider group-hover:text-blue-300">Trace ID</span>
                                        <p className="text-gray-400 font-mono mt-1 text-xs truncate group-hover:text-white group-hover:underline" title={result.request_id}>
                                            {result.request_id || '--'}
                                        </p>
                                    </div>
                                </div>

                                {/* Rich Sources List */}
                                <SourcesList chunks={result.grounding?.chunks || []} />

                                {/* Debug Details (JSON) */}
                                <div className="border-t border-white/10 pt-4">
                                    <details className="group">
                                        <summary className="flex items-center cursor-pointer text-xs text-gray-500 hover:text-white transition-colors">
                                            <ChevronDown className="w-3 h-3 mr-2 group-open:rotate-180 transition-transform" />
                                            Ver Raw RAG Response (JSON)
                                        </summary>
                                        <div className="mt-4 p-4 bg-black/60 border border-white/5 rounded-lg overflow-auto max-h-96">
                                            <pre className="text-[10px] font-mono text-gray-400">
                                                {JSON.stringify(result, null, 2)}
                                            </pre>
                                        </div>
                                    </details>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
